/**
 * Unit conversion for ingredient costing.
 *
 * Every ingredient has exactly one canonical base unit — `g`, `ml`, or `each`.
 * You buy flour in 50 lb bags and use it in grams, so every purchase records a
 * conversion into base units and cost is always carried as *cost per base unit*.
 * That is what lets a price change on a bag of flour land correctly on a single
 * pizza.
 *
 * The factors come from the unit catalog (catalog.ts) and are exact by definition
 * (international avoirdupois pound, US customary and imperial fluid measures), not
 * rounded approximations — a sloppy lb→g factor produces margins that look
 * confident and are wrong. For ingredient-aware conversion (cups of honey to grams,
 * packages, counted items) use `toBaseQuantity` in conversions.ts.
 */

import { UNIT_CATALOG, getUnit, type UnitCode, type UnitKind } from './catalog.ts';

export type BaseUnit = 'g' | 'ml' | 'each';

type CatalogUnit = (typeof UNIT_CATALOG)[number];
export type MassUnit = Extract<CatalogUnit, { kind: 'mass' }>['code'];
export type VolumeUnit = Extract<CatalogUnit, { kind: 'volume' }>['code'];
export type CountUnit = Extract<CatalogUnit, { kind: 'count' }>['code'];
export type Unit = UnitCode;

const BASE_OF_KIND: Record<UnitKind, BaseUnit> = { mass: 'g', volume: 'ml', count: 'each' };
const KIND_OF_BASE: Record<BaseUnit, UnitKind> = { g: 'mass', ml: 'volume', each: 'count' };

/** Thrown when a conversion is not physically meaningful. */
export class UnitConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnitConversionError';
  }
}

export function baseUnitOfKind(kind: UnitKind): BaseUnit {
  return BASE_OF_KIND[kind];
}

export function kindOfBaseUnit(base: BaseUnit): UnitKind {
  return KIND_OF_BASE[base];
}

export function dimensionOf(unit: Unit): BaseUnit {
  const def = getUnit(unit);
  if (!def) throw new UnitConversionError(`Unknown unit: ${String(unit)}`);
  return BASE_OF_KIND[def.kind];
}

export function isUnit(value: string): value is Unit {
  return getUnit(value) !== undefined;
}

function factorFor(unit: Unit): number {
  const def = getUnit(unit);
  if (!def) throw new UnitConversionError(`Unknown unit: ${String(unit)}`);
  return def.toBase;
}

/**
 * Density lets an ingredient cross between mass and volume — olive oil bought
 * by the litre but used by the gram, for instance. Without one, a mass↔volume
 * conversion throws rather than silently assuming water.
 */
export interface Density {
  /** Grams per millilitre. Water is 1.0; olive oil ≈ 0.915; "00" flour ≈ 0.509 (120.5 g per cup). */
  gramsPerMl: number;
}

/**
 * Convert a quantity into an ingredient's canonical base unit.
 *
 * @throws {UnitConversionError} if the unit's dimension differs from the base
 * unit and no density bridges them, or if a count is converted to a measure.
 */
export function toBaseUnits(
  qty: number,
  unit: Unit,
  baseUnit: BaseUnit,
  density?: Density,
): number {
  if (!Number.isFinite(qty)) {
    throw new UnitConversionError(`Quantity must be a finite number, got ${qty}`);
  }

  const dimension = dimensionOf(unit);
  const inDimension = qty * factorFor(unit);

  if (dimension === baseUnit) return inDimension;

  // `each` cannot be derived from mass or volume here, nor the reverse. Use
  // `toBaseQuantity` with the ingredient's each_weight_g for counted items.
  if (dimension === 'each' || baseUnit === 'each') {
    throw new UnitConversionError(
      `Cannot convert ${unit} to ${baseUnit}: counted units have no mass or volume equivalent. ` +
        `Give the ingredient a base unit of '${dimension}', or record the purchase in ${baseUnit}.`,
    );
  }

  if (!density) {
    throw new UnitConversionError(
      `Cannot convert ${unit} to ${baseUnit} without a density. ` +
        `Set gramsPerMl on the ingredient to convert between mass and volume.`,
    );
  }
  if (!(density.gramsPerMl > 0)) {
    throw new UnitConversionError(`Density must be greater than zero, got ${density.gramsPerMl}`);
  }

  // dimension === 'g' && baseUnit === 'ml', or the reverse.
  return dimension === 'g' ? inDimension / density.gramsPerMl : inDimension * density.gramsPerMl;
}

/** Convert between any two compatible units. */
export function convert(qty: number, from: Unit, to: Unit, density?: Density): number {
  const inBase = toBaseUnits(qty, from, dimensionOf(to), density);
  return inBase / factorFor(to);
}

/**
 * Cost per base unit for a purchase — the number every recipe cost is built on.
 *
 * @example
 * // A 50 lb bag of flour at $27.50
 * costPerBaseUnit({ totalCost: 27.5, qty: 50, unit: 'lb', baseUnit: 'g' })
 * // => 0.00121... dollars per gram
 */
export function costPerBaseUnit(input: {
  /** What was paid, in currency, for the whole purchase line. */
  totalCost: number;
  /** How much was bought, in `unit`. */
  qty: number;
  unit: Unit;
  baseUnit: BaseUnit;
  density?: Density;
}): number {
  const { totalCost, qty, unit, baseUnit, density } = input;
  if (!Number.isFinite(totalCost) || totalCost < 0) {
    throw new UnitConversionError(`Total cost must be a non-negative number, got ${totalCost}`);
  }
  const baseQty = toBaseUnits(qty, unit, baseUnit, density);
  if (baseQty <= 0) {
    throw new UnitConversionError(
      `Purchase quantity must be greater than zero, got ${qty} ${unit}`,
    );
  }
  return totalCost / baseQty;
}

/** Human labels for unit pickers in the UI. */
export const UNIT_LABELS = Object.fromEntries(UNIT_CATALOG.map((u) => [u.code, u.name])) as Record<
  Unit,
  string
>;

/** Units of the same kind as a base unit, in catalog order, for populating a picker. */
export function unitsFor(baseUnit: BaseUnit): Unit[] {
  const kind = KIND_OF_BASE[baseUnit];
  return [...UNIT_CATALOG]
    .filter((u) => u.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((u) => u.code);
}
